/**
 * Role → required onboarding steps map.
 *
 * Driven server-side by the canonical KYC catalogue in compliance-service,
 * but mirrored here so the wizard knows what to render before the user's
 * `/kyc/me` response comes back. Mismatches between this map and the
 * server's gating logic are caught at submit time (the per-action gate
 * still rejects with 403 if a required item is missing).
 *
 * Conventions:
 *  - `key` matches the wizard step component name (one switch table)
 *  - `label` is the user-facing step indicator label
 *  - `mandatory` items must be completed before the user can leave the
 *    wizard. Optional items can be skipped.
 *
 * Per-role overrides:
 *  - For HOST users, only PAN + Aadhaar are gating; GSTIN, bank account,
 *    address proof and trade certificates are optional. The host can
 *    create + list a warehouse without them. We keep those steps in the
 *    HOST flow so the host *can* complete them later (GSTIN unlocks GST
 *    invoicing; bank unlocks payouts), they just don't block listing.
 *  - LOGISTICS_PARTNER keeps the full gate (DL, RC, bank, GSTIN) — they
 *    can't earn until paid out, and that needs all four.
 */

export const STEP_KEYS = {
  KYC_CONSENT: 'kyc_consent',
  PAN: 'pan',
  AADHAAR: 'aadhaar',
  GSTIN: 'gstin',
  BANK: 'bank',
  ADDRESS_PROOF: 'address_proof',
  DRIVING_LICENSE: 'driving_license',
  VEHICLE_RC: 'vehicle_rc',
};

/** Codes inside the consent catalogue that the wizard requires
 *  before it can call any Sandbox verification endpoint. The backend's
 *  KYC use-cases enforce the same pair (see compliance-service
 *  ``_ensure_kyc_consent``) — we re-check client-side so the user
 *  only ever sees the "grant consent" screen, not a 403. */
export const KYC_CONSENT_CODES = ['kyc_data_processing', 'third_party_kyc_sandbox'];

const STEP_DEFS = {
  [STEP_KEYS.KYC_CONSENT]: {
    label: 'Consent',
    title: 'Permission to verify your identity',
    mandatory: true,
  },
  [STEP_KEYS.PAN]: {
    label: 'PAN',
    title: 'Verify your PAN',
    mandatory: true,
  },
  [STEP_KEYS.AADHAAR]: {
    label: 'Aadhaar',
    title: 'Verify your Aadhaar',
    mandatory: true,
  },
  [STEP_KEYS.GSTIN]: {
    label: 'GSTIN',
    title: 'Add your GSTIN',
    mandatory: true,
  },
  [STEP_KEYS.BANK]: {
    label: 'Bank account',
    title: 'Add your payout bank account',
    mandatory: true,
  },
  [STEP_KEYS.ADDRESS_PROOF]: {
    label: 'Address proof',
    title: 'Upload an address proof',
    mandatory: false,
  },
  [STEP_KEYS.DRIVING_LICENSE]: {
    label: 'Driving licence',
    title: 'Upload your driving licence',
    mandatory: true,
  },
  [STEP_KEYS.VEHICLE_RC]: {
    label: 'Vehicle RC',
    title: 'Upload your vehicle RC',
    mandatory: true,
  },
};

/**
 * Returns the ordered step list for the given role. Every role gets
 * KYC_CONSENT as the first step — the wizard auto-skips it when the
 * user has already granted both required consents. Roles unrecognised
 * by the wizard fall back to the lightest possible flow (consent +
 * PAN + Aadhaar) so existing admin / staff users don't get stuck.
 */
export const stepsForRole = (rawRole) => {
  const role = String(rawRole || '').toUpperCase();
  switch (role) {
    case 'HOST':
      return [
        STEP_KEYS.KYC_CONSENT,
        STEP_KEYS.PAN,
        STEP_KEYS.AADHAAR,
        STEP_KEYS.GSTIN,
        STEP_KEYS.BANK,
        STEP_KEYS.ADDRESS_PROOF,
      ];
    case 'LOGISTICS_PARTNER':
      return [
        STEP_KEYS.KYC_CONSENT,
        STEP_KEYS.PAN,
        STEP_KEYS.AADHAAR,
        STEP_KEYS.GSTIN,
        STEP_KEYS.BANK,
        STEP_KEYS.DRIVING_LICENSE,
        STEP_KEYS.VEHICLE_RC,
      ];
    case 'TENANT':
      return [
        STEP_KEYS.KYC_CONSENT,
        STEP_KEYS.PAN,
        STEP_KEYS.AADHAAR,
      ];
    default:
      // ADMIN / STAFF / unknown → light flow.
      return [
        STEP_KEYS.KYC_CONSENT,
        STEP_KEYS.PAN,
        STEP_KEYS.AADHAAR,
      ];
  }
};

export const stepDef = (key) => STEP_DEFS[key];

/**
 * Per-role overrides that downgrade a step's `mandatory: true` to
 * effectively-optional. Centralised here so the wizard, banner and
 * route guard agree on what counts as "done" for a given role.
 *
 * Why a separate map (instead of duplicating STEP_DEFS per role):
 * the underlying step definition is identical across roles — same
 * label, same satisfaction check. Only the gating decision differs,
 * and gating is a role-scoped policy, not a property of the step.
 */
const OPTIONAL_FOR_ROLE = {
  HOST: new Set([STEP_KEYS.GSTIN, STEP_KEYS.BANK]),
};

/**
 * True iff `stepKey` is mandatory for `role`. Combines the global
 * `mandatory` flag from STEP_DEFS with any per-role override above.
 */
export const isStepMandatoryForRole = (rawRole, stepKey) => {
  const def = stepDef(stepKey);
  if (!def?.mandatory) return false;
  const role = String(rawRole || '').toUpperCase();
  const overrides = OPTIONAL_FOR_ROLE[role];
  if (overrides && overrides.has(stepKey)) return false;
  return true;
};

/** True iff every code in ``KYC_CONSENT_CODES`` has an active
 *  (granted, not revoked) record in ``consents``. */
export const hasKycConsents = (consents) => {
  const byCode = new Map();
  for (const c of consents || []) {
    // Most-recent record wins (server returns desc).
    if (!byCode.has(c.consent_type)) byCode.set(c.consent_type, c);
  }
  return KYC_CONSENT_CODES.every((code) => {
    const r = byCode.get(code);
    return !!(r && r.consent_given && !r.revoked_at);
  });
};

/**
 * Given the user's existing KYC verifications, uploaded docs, bank
 * accounts and consent records, decide which steps are already
 * satisfied. Used to pre-skip steps the user already completed (e.g.
 * they verified PAN earlier and are returning to finish bank setup).
 *
 * @param {string} stepKey
 * @param {{
 *   kyc: Array<{verification_type: string, status: string, revoked_at: string|null}>,
 *   docs: Array<{category: string, subtype: string, status: string}>,
 *   bankAccounts: Array<unknown>,
 *   consents: Array<{consent_type: string, consent_given: boolean, revoked_at: string|null}>,
 * }} state
 */
export const isStepSatisfied = (stepKey, state) => {
  const { kyc = [], docs = [], bankAccounts = [], consents = [] } = state || {};
  const isKycActive = (type) =>
    kyc.some(
      (k) =>
        k.verification_type === type &&
        k.status === 'verified' &&
        !k.revoked_at,
    );
  const hasDoc = (category, subtype) =>
    docs.some((d) => d.category === category && d.subtype === subtype);

  switch (stepKey) {
    case STEP_KEYS.KYC_CONSENT:
      return hasKycConsents(consents);
    case STEP_KEYS.PAN:
      return isKycActive('pan');
    case STEP_KEYS.AADHAAR:
      return isKycActive('aadhaar');
    case STEP_KEYS.GSTIN:
      return isKycActive('gstin');
    case STEP_KEYS.BANK:
      return Array.isArray(bankAccounts) && bankAccounts.length > 0;
    case STEP_KEYS.ADDRESS_PROOF:
      return hasDoc('address_proof', 'utility_bill')
        || hasDoc('address_proof', 'rental_agreement')
        || hasDoc('address_proof', 'bank_statement');
    case STEP_KEYS.DRIVING_LICENSE:
      return hasDoc('vehicle', 'driving_license');
    case STEP_KEYS.VEHICLE_RC:
      return hasDoc('vehicle', 'rc_book');
    default:
      return false;
  }
};

/**
 * True iff every mandatory step for `role` is already satisfied —
 * the wizard's "you're done" check, also used by the route guard to
 * decide whether the user needs to land on the wizard at all.
 *
 * Uses `isStepMandatoryForRole` so per-role overrides (HOST drops
 * GSTIN + bank) flow through automatically.
 */
export const isOnboardingComplete = (role, state) => {
  const steps = stepsForRole(role);
  return steps.every((key) => {
    if (!isStepMandatoryForRole(role, key)) return true;
    return isStepSatisfied(key, state);
  });
};
